'use strict';
import isFunction from 'lodash/isFunction';
import React from 'react';
import * as T from 'prop-types';
import {AbstractForm} from '../../../components/forms/AbstractForm';
import {ButtonState, isButtonState} from '../../../core-utilities/enums/ButtonState';
import {AuthModalMode} from '../../enums/AuthModalMode';
import {allowedModesPropType, AuthFormView} from './AuthFormView';

export class AuthFormComponent extends AbstractForm {
  static propTypes = {
    className: T.string,
    allowedModes: allowedModesPropType,
    initialMode: allowedModesPropType,
    showCloseButton: T.bool,
    loginUserMode: isButtonState,
    loginUserErrorCode: T.string,
    resetPasswordMode: isButtonState,
    signupUserMode: isButtonState,
    model: T.object,

    onLogin: T.func.isRequired,
    onResetPassword: T.func.isRequired,
    onSignup: T.func.isRequired,
    onCancel: T.func,
  };

  static defaultProps = {
    allowedModes: AuthModalMode.LOGIN_AND_SIGNUP,
    initialMode: AuthModalMode.LOGIN,
    showCloseButton: false,
    model: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      currentMode: this.props.initialMode,

      acceptTosAndPrivacy: false,

      emailError: false,
      passwordError: false,
      firstNameError: false,
      lastNameError: false,
      nameError: false,
    };

    this.handleChangeEmail = this.handleChangeEmail.bind(this);
    this.handleValidationChangeEmail = this.handleValidationChangeEmail.bind(this);
    this.handleChangePassword = this.handleChangePassword.bind(this);
    this.handleValidationChangePassword = this.handleValidationChangePassword.bind(this);
    this.handleChangePasswordConfirm = this.handleChangePasswordConfirm.bind(this);
    this.handleValidationChangePasswordConfirm = this.handleValidationChangePasswordConfirm.bind(this);
    this.handleValidatePasswordConfirm = this.handleValidatePasswordConfirm.bind(this);
    this.handleChangeFirstName = this.handleChangeFirstName.bind(this);
    this.handleValidationChangeFirstName = this.handleValidationChangeFirstName.bind(this);
    this.handleChangeLastName = this.handleChangeLastName.bind(this);
    this.handleValidationChangeLastName = this.handleValidationChangeLastName.bind(this);
    this.handleChangeAcceptTosAndPrivacy = this.handleChangeAcceptTosAndPrivacy.bind(this);
    this.handleClickShowResetPassword = this.handleClickShowResetPassword.bind(this);
    this.handleClickShowLogin = this.handleClickShowLogin.bind(this);
    this.handleClickShowSignup = this.handleClickShowSignup.bind(this);
    this.handleClickOkay = this.handleClickOkay.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  /**
   * @param event
   * @private
   */
  handleChangeEmail(event) {
    this.handleValueChange(event, 'email', 'isChangingEmail');
  }

  /**
   * @param event
   * @private
   */
  handleValidationChangeEmail(event) {
    this.handleValidationChange(event, 'emailError');
  }

  /**
   * @param event
   * @private
   */
  handleChangePassword(event) {
    this.handleValueChange(event, 'password', 'isChangingPassword');
  }

  /**
   * @param event
   * @private
   */
  handleValidationChangePassword(event) {
    this.handleValidationChange(event, 'passwordError');
  }

  /**
   * @param event
   * @private
   */
  handleChangePasswordConfirm(event) {
    this.handleValueChange(event, 'passwordConfirm', 'isChangingPasswordConfirm');
  }

  /**
   * @param event
   * @private
   */
  handleValidationChangePasswordConfirm(event) {
    this.handleValidationChange(event, 'passwordConfirmError');
  }

  handleValidatePasswordConfirm({prev, next}) {
    if (next !== this.state.password) {
      return {
        value: next,
        error: {code: 'onboarding.join.password.confirm.match.message'},
      };
    }
  }

  /**
   * @param event
   * @private
   */
  handleChangeFirstName(event) {
    this.handleValueChange(event, 'firstName', 'isChangingFirstName');
  }

  /**
   * @param event
   * @private
   */
  handleValidationChangeFirstName(event) {
    this.handleValidationChange(event, 'firstNameError');
  }

  /**
   * @param event
   * @private
   */
  handleChangeLastName(event) {
    this.handleValueChange(event, 'lastName', 'isChangingLastName');
  }

  /**
   * @param event
   * @private
   */
  handleValidationChangeLastName(event) {
    this.handleValidationChange(event, 'lastNameError');
  }

  /**
   * @param event
   * @private
   */
  handleChangeAcceptTosAndPrivacy(event) {
    this.setState({
      acceptTosAndPrivacy: !this.state.acceptTosAndPrivacy,
    });
  }

  handleClickShowLogin(event) {
    event.stopPropagation();
    event.preventDefault();

    this.setState({
      currentMode: AuthModalMode.LOGIN,
    });
  }

  handleClickShowResetPassword(event) {
    event.stopPropagation();
    event.preventDefault();

    this.setState({
      currentMode: AuthModalMode.FORGOT_PASSWORD,
    });
  }

  handleClickShowSignup(event) {
    event.stopPropagation();
    event.preventDefault();

    this.setState({
      currentMode: AuthModalMode.SIGNUP,
    });
  }

  /**
   * @param event
   * @private
   */
  handleClickOkay(event) {
    event.stopPropagation();
    event.preventDefault();

    if (!this.isValid()) {
      return;
    }

    switch (this.state.currentMode) {
      case AuthModalMode.LOGIN:
        if (isFunction(this.props.onLogin)) {
          this.props.onLogin(this.serializeModel());
        }
        break;

      case AuthModalMode.FORGOT_PASSWORD:
        if (isFunction(this.props.onResetPassword)) {
          this.props.onResetPassword(this.serializeModel());
        }
        break;

      case AuthModalMode.SIGNUP:
        if (isFunction(this.props.onSignup)) {
          this.props.onSignup(this.serializeModel());
        }
        break;
    }
  }

  /**
   * @param event
   * @private
   */
  handleCancel(event) {
    event.stopPropagation();
    event.preventDefault();

    if (isFunction(this.props.onCancel)) {
      this.props.onCancel(null);
    }
  }

  render() {
    const okayButtonMode = this.getOkayButtonMode();

    return (
      <AuthFormView
        className={this.props.className}
        isModal={this.props.isModal}
        keyboardHeight={this.props.keyboardHeight}
        keyboardStyle={this.props.keyboardStyle}
        allowedModes={this.props.allowedModes}
        currentMode={this.state.currentMode}
        showCloseButton={this.props.showCloseButton}
        email={null}
        displayName={this.getDisplayName()}
        firstName={null}
        lastName={null}
        acceptTosAndPrivacy={this.state.acceptTosAndPrivacy}

        onChangeEmail={this.handleChangeEmail}
        onValidationChangeEmail={this.handleValidationChangeEmail}
        onChangePassword={this.handleChangePassword}
        onValidationChangePassword={this.handleValidationChangePassword}
        onChangePasswordConfirm={this.handleChangePasswordConfirm}
        onValidationChangePasswordConfirm={this.handleValidationChangePasswordConfirm}
        onValidatePasswordConfirm={this.handleValidatePasswordConfirm}
        onChangeFirstName={this.handleChangeFirstName}
        onValidationChangeFirstName={this.handleValidationChangeFirstName}
        onChangeLastName={this.handleChangeLastName}
        onValidationChangeLastName={this.handleValidationChangeLastName}
        onChangeAcceptTosAndPrivacy={this.handleChangeAcceptTosAndPrivacy}
        onClickShowResetPassword={this.handleClickShowResetPassword}
        onClickShowLogin={this.handleClickShowLogin}
        onClickShowSignup={this.handleClickShowSignup}
        onClickOkay={this.handleClickOkay}
        onCancel={this.handleCancel}

        isSaveDisabled={!this.isValid()}
        isFetching={okayButtonMode === ButtonState.WORKING}
        okayButtonMode={okayButtonMode}
        errorCode={this.getErrorCode()}
      />
    );
  }

  getDisplayName() {
    return `${this.state.firstName || ''} ${this.state.lastName || ''}`;
  }

  getOkayButtonMode() {
    switch (this.state.currentMode) {
      case AuthModalMode.LOGIN:
        return this.props.loginUserMode;

      case AuthModalMode.FORGOT_PASSWORD:
        return this.props.resetPasswordMode;

      case AuthModalMode.SIGNUP:
        return this.props.signupUserMode;
    }
  }

  getErrorCode() {
    switch (this.state.currentMode) {
      case AuthModalMode.LOGIN:
        return this.props.loginUserErrorCode;

      // case AuthModalMode.FORGOT_PASSWORD:
      //   return this.props.resetPasswordMode;
      //
      // case AuthModalMode.SIGNUP:
      //   return this.props.signupUserMode;
    }
  }

  isValid() {
    if (this.state.currentMode === AuthModalMode.SIGNUP) {
      return this.state.email
        && this.state.password
        && this.state.password === this.state.passwordConfirm
        && this.state.firstName
        && this.state.lastName
        && this.state.acceptTosAndPrivacy
      ;
    } else if (this.state.currentMode === AuthModalMode.FORGOT_PASSWORD) {
      return !!this.state.email;
    } else {
      return true;
    }
  }

  /**
   * Whether any changes have been made to the model
   * @return {boolean}
   */
  isDirty() {
    return true;
  }

  serializeModel() {
    const modelUpdate = {};
    modelUpdate.email = this.state.email;

    if (this.state.currentMode === AuthModalMode.LOGIN) {
      modelUpdate.password = this.state.password;

    } else if (this.state.currentMode === AuthModalMode.SIGNUP) {
      modelUpdate.password = this.state.password;
      modelUpdate.firstName = this.state.firstName;
      modelUpdate.lastName = this.state.lastName;
      modelUpdate.displayName = this.getDisplayName();
      modelUpdate.acceptTosAndPrivacy = this.state.acceptTosAndPrivacy;
    }

    return modelUpdate;
  }
}



// WEBPACK FOOTER //
// ./src/js/auth/components/AuthForm/AuthFormComponent.js